<template>
  <div
    class="flex items-center gap-3"
    data-test="message-view-list-item"
  >
    <contact-avatar
      v-if="contact"
      :contact="contact"
      :contact-type="contact.type"
      :size="40"
      class="shrink-0"
    />
    <contact-avatar
      v-else
      :name="displayName"
      :size="40"
      class="shrink-0"
    />
    <div class="min-w-0 grow">
      <p
        class="truncate text-body-m-14-20 text-fg-base-fg2"
        data-test="message-view-list-item-name"
      >
        <template v-if="isIAm">
          {{ displayName }} ({{ $t('common.you') }})
        </template>
        <template v-else>
          {{ displayName }}
          <template v-if="isGuestAuthor">
            ({{ $t('common.guest') }})
          </template>
        </template>
      </p>
      <p
        v-if="formattedTime"
        class="mt-0.5 flex items-center gap-1 text-label-r-12-18 text-fg-base-fg14"
        data-test="message-view-list-item-time"
      >
        <wui-icon
          name="checksM"
          class="size-3.5! shrink-0 text-fg-base-fg14"
        />
        <span>{{ formattedTime }}</span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { WuiIcon } from '@wui/common-library'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { useStoreContacts } from '@/entities/contact'
import { ContactAvatar } from '@/entities/contact-avatar'
import { useStoreRooms } from '@/entities/room'

import { useCurrentUser } from '@/shared/composables/current-user'
import { useGuestAuth } from '@/shared/composables/guest-auth'
import { isGuest } from '@/shared/composables/local-storage'
import { Contact } from '@/shared/types/api'
import { GUEST } from '@/shared/utils/constants'
import { getUnknownContactLabel } from '@/shared/utils/contacts'
import { formatRelativeDateTimeIn } from '@/shared/utils/date-time'

const props = defineProps<{
  author: string
  timestamp?: number | undefined
}>()

const { findByPServed, findGuestById } = useStoreContacts()
const roomsStore = useStoreRooms()
const { rooms } = storeToRefs(roomsStore)
const { currentUser } = useCurrentUser()
const { currentGuest } = useGuestAuth()

const isCurrentUser = computed(() => props.author === currentUser.value?.imLogin)

const contact = computed<Contact | undefined>(() => {
  if (isCurrentUser.value) return currentUser.value
  if (props.author.includes(GUEST)) return undefined
  return isGuest() ? findGuestById(props.author) : findByPServed(props.author)
})

const guest = computed<Contact | { name: string } | undefined>(() => {
  if (!props.author.includes(GUEST)) return undefined

  for (const [, room] of rooms.value) {
    const guestName = roomsStore.findGuestByPserved(room.pServedUser, props.author)
    if (guestName) {
      return { name: guestName }
    }
  }

  return findGuestById(props.author)
})

const isCurrentGuest = computed(() => {
  if (!currentGuest.value || !isGuest()) return false
  if (props.author === currentGuest.value.pServed) return true
  const guestContact = guest.value as Contact | undefined
  return guestContact?.imLogin === currentGuest.value.pServed
})

const isIAm = computed(() => isCurrentUser.value || isCurrentGuest.value)
const isGuestAuthor = computed(() => props.author.includes(GUEST))

const displayName = computed(() => {
  if (contact.value) {
    const name = contact.value.lastName
      ? `${contact.value.lastName} ${contact.value.firstName}`
      : contact.value.name
    if (name) return name
  }

  if (guest.value?.name) {
    return guest.value.name
  }

  if (isCurrentGuest.value && currentGuest.value?.login) {
    return currentGuest.value.login
  }

  if (isCurrentUser.value && currentUser.value?.imLogin) {
    return currentUser.value.imLogin
  }

  return getUnknownContactLabel(props.author)
})

const formattedTime = computed(() => formatRelativeDateTimeIn(props.timestamp))
</script>
